import { create } from "zustand";
import type { MediaPlayerInstance } from "@vidstack/react";
import { PodcastService } from "../podcast/podcast.service";
import type { PodcastChapter } from "../podcast/podcast.service";
import { PodcastPlaybackProgressService } from "../podcast/podcastPlaybackProgress.service";

/**
 * Represents a chapter/section in the media file
 */
export interface Chapter {
	/** Start time of the chapter in seconds */
	startTime: number;
	/** End time of the chapter in seconds */
	endTime: number;
	/** Chapter title/description */
	text: string;
}

/**n
 * Represents a media item with metadata
 */
export interface MediaItem {
	/** Source URL of the media file */
	src: string;
	/** Media title */
	title: string;
	/** Optional subtitle (e.g., podcast show name) */
	subtitle?: string;
	/** Optional artwork/cover image URL */
	artwork?: string;
	/** Optional array of chapters */
	chapters?: Chapter[];
}

/**
 * State interface for the player store
 */
interface PlayerState {
	// ========================================
	// Core Player State
	// ========================================

	/** Reference to the Vidstack MediaPlayer instance */
	playerRef: MediaPlayerInstance | null;

	/** Whether the media is currently paused */
	isPaused: boolean;

	/** Whether the media is currently playing (inverse of isPaused for convenience) */
	isPlaying: boolean;

	/** Current playback position in seconds */
	currentTime: number;

	/** Total duration of the media in seconds */
	duration: number;

	/** End position of the buffered range in seconds */
	bufferedEnd: number;

	// ========================================
	// Audio Control State
	// ========================================

	/** Volume level (0.0 to 1.0) */
	volume: number;

	/** Whether audio is muted */
	isMuted: boolean;

	/** Current playback speed/rate (e.g., 1.0 = normal, 1.5 = 1.5x speed) */
	playbackSpeed: number;

	// ========================================
	// Media & Chapter State
	// ========================================

	/** Currently loaded media with metadata */
	currentMedia: MediaItem | null;

	/** ID of the currently loaded podcast (null if not a podcast) */
	podcastId: string | null;

	/** ID of the pod workspace associated with the current podcast */
	podWorkspaceId: string | null;

	/** Array of chapters in the current media (transformed for player) */
	chapters: Chapter[];

	/** Original podcast chapters with full metadata (including status) */
	podcastChapters: PodcastChapter[];

	/** Index of the currently active chapter (-1 if no active chapter) */
	activeChapterIndex: number;

	/** Whether the chapter list UI is currently visible */
	showChapters: boolean;

	/** Whether the speed menu UI is currently visible */
	showSpeedMenu: boolean;

	/** Whether to auto-play when media is ready */
	autoPlay: boolean;

	/**
	 * Pending position restoration for a specific podcast.
	 * This is set when loading a podcast and cleared after restoration.
	 * Structure: { podcastId, position } or null if no restoration needed
	 */
	pendingRestore: { podcastId: string; position: number } | null;

	// ========================================
	// Actions
	// ========================================

	/**
	 * Set the Vidstack player instance reference
	 * This should be called once when the player component mounts
	 */
	setPlayerRef: (ref: MediaPlayerInstance | null) => void;

	/**
	 * Start media playback
	 */
	play: () => void;

	/**
	 * Pause media playback
	 */
	pause: () => void;

	/**
	 * Toggle between play and pause states
	 */
	togglePlay: () => void;

	/**
	 * Seek to a specific time position
	 * @param time - Target time in seconds
	 */
	seek: (time: number) => void;

	/**
	 * Seek relative to the current position
	 * @param seconds - Number of seconds to skip (positive for forward, negative for backward)
	 */
	seekRelative: (seconds: number) => void;

	/**
	 * Set the volume level
	 * @param volume - Volume level between 0.0 and 1.0
	 */
	setVolume: (volume: number) => void;

	/**
	 * Toggle mute state
	 */
	toggleMute: () => void;

	/**
	 * Set the playback speed/rate
	 * @param speed - Playback rate (e.g., 0.5, 1.0, 1.5, 2.0)
	 */
	setPlaybackSpeed: (speed: number) => void;

	/**
	 * Load a new media item with metadata
	 * @param media - Media item to load
	 */
	loadMedia: (media: MediaItem) => void;

	/**
	 * Load a podcast by ID from the backend
	 * Fetches the podcast data, audio file, and chapters
	 * @param podcastId - The ID of the podcast to load
	 * @param autoPlay - Whether to automatically play when media is ready
	 * @param restoreProgress - Whether to restore playback progress from last listen (default: true)
	 * @returns Promise that resolves when the podcast is loaded
	 */
	loadPodcast: (
		podcastId: string,
		autoPlay?: boolean,
		restoreProgress?: boolean,
	) => Promise<void>;

	/**
	 * Jump to the next chapter
	 */
	goToNextChapter: () => void;

	/**
	 * Jump to the previous chapter
	 */
	goToPreviousChapter: () => void;

	/**
	 * Jump to a specific chapter by index
	 * @param index - Chapter index (0-based)
	 */
	goToChapter: (index: number) => void;

	/**
	 * Set whether the chapter list is visible
	 * @param show - True to show, false to hide
	 */
	setShowChapters: (show: boolean) => void;

	/**
	 * Toggle the chapter list visibility
	 */
	toggleChaptersList: () => void;

	/**
	 * Toggle the speed menu visibility
	 */
	toggleSpeedMenu: () => void;

	// ========================================
	// State Update Methods (called by player component)
	// ========================================

	/**
	 * Update the playback state (called by player component)
	 * @param isPaused - Current paused state from Vidstack
	 */
	updatePlaybackState: (isPaused: boolean) => void;

	/**
	 * Update the current time (called by player component)
	 * @param time - Current time from Vidstack
	 */
	updateCurrentTime: (time: number) => void;

	/**
	 * Update the duration (called by player component)
	 * @param duration - Duration from Vidstack
	 */
	updateDuration: (duration: number) => void;

	/**
	 * Update the buffered end position (called by player component)
	 * @param bufferedEnd - Buffered end position from Vidstack
	 */
	updateBufferedEnd: (bufferedEnd: number) => void;

	/**
	 * Update the volume (called by player component)
	 * @param volume - Volume from Vidstack
	 */
	updateVolume: (volume: number) => void;

	/**
	 * Update the mute state (called by player component)
	 * @param isMuted - Mute state from Vidstack
	 */
	updateMuteState: (isMuted: boolean) => void;

	/**
	 * Update the playback speed (called by player component)
	 * @param speed - Playback rate from Vidstack
	 */
	updatePlaybackSpeed: (speed: number) => void;

	/**
	 * Update the active chapter index based on current time
	 * @param time - Current time to calculate active chapter
	 */
	updateActiveChapter: (time: number) => void;

	// ========================================
	// Utility Methods
	// ========================================

	/**
	 * Format a time value in seconds to MM:SS format
	 * @param seconds - Time in seconds
	 * @returns Formatted time string (e.g., "12:34")
	 */
	formatTime: (seconds: number) => string;
}

/**
 * Zustand store for managing the Vidstack media player state and actions.
 *
 * This store provides a centralized way to control and observe the player state
 * from anywhere in the application. It syncs with the Vidstack player instance
 * and exposes all common player operations.
 *
 * @example
 * // Control the player
 * const actions = usePlayerActions();
 * actions.play();
 * actions.seek(30);
 * actions.setPlaybackSpeed(1.5);
 *
 * @example
 * // Read player state (with optimized re-renders)
 * const isPaused = usePlayerStore((state) => state.isPaused);
 * const currentTime = usePlayerStore((state) => state.currentTime);
 */
export const usePlayerStore = create<PlayerState>((set, get) => ({
	// Initial state
	playerRef: null,
	isPaused: true,
	isPlaying: false,
	currentTime: 0,
	duration: 0,
	bufferedEnd: 0,
	volume: 1,
	isMuted: false,
	playbackSpeed: 1,
	currentMedia: null,
	podcastId: null,
	podWorkspaceId: null,
	chapters: [],
	podcastChapters: [],
	activeChapterIndex: -1,
	showChapters: false,
	showSpeedMenu: false,
	autoPlay: false,
	pendingRestore: null,

	// Core actions
	setPlayerRef: (ref) => set({ playerRef: ref }),

	play: () => {
		const { playerRef } = get();
		if (!playerRef) return;

		// Optimistically update playback state so UI reacts immediately.
		// Vidstack state sync (usePlayerSync) will keep this accurate.
		set({ isPaused: false, isPlaying: true });
		playerRef.play();
	},

	pause: () => {
		const { playerRef } = get();
		if (!playerRef) return;

		// Optimistically update playback state so UI reacts immediately.
		set({ isPaused: true, isPlaying: false });
		playerRef.pause();
	},

	togglePlay: () => {
		const { playerRef, isPaused } = get();
		if (!playerRef) return;

		if (isPaused) {
			// Toggle from paused -> playing.
			set({ isPaused: false, isPlaying: true });
			playerRef.play();
		} else {
			// Toggle from playing -> paused.
			set({ isPaused: true, isPlaying: false });
			playerRef.pause();
		}
	},

	seek: (time) => {
		const { playerRef, duration } = get();
		if (playerRef && Number.isFinite(time)) {
			// Clamp time to valid range
			const clampedTime = Math.max(0, Math.min(time, duration));
			playerRef.currentTime = clampedTime;
		}
	},

	seekRelative: (seconds) => {
		const { currentTime } = get();
		get().seek(currentTime + seconds);
	},

	setVolume: (volume) => {
		const { playerRef } = get();
		if (playerRef) {
			// Clamp volume to valid range
			const clampedVolume = Math.max(0, Math.min(1, volume));
			playerRef.volume = clampedVolume;
		}
	},

	toggleMute: () => {
		const { playerRef, isMuted } = get();
		if (playerRef) {
			playerRef.muted = !isMuted;
		}
	},

	setPlaybackSpeed: (speed) => {
		const { playerRef } = get();
		if (playerRef && speed > 0) {
			playerRef.playbackRate = speed;
		}
	},

	loadMedia: (media) => {
		// Update store with media metadata
		// The player component will handle updating the actual src attribute
		set({
			currentMedia: media,
			// Reset podcast-specific state for generic media
			podcastId: null, // Clear podcast ID for non-podcast media
			podWorkspaceId: null, // Clear pod workspace ID for non-podcast media
			chapters: media.chapters || [],
			podcastChapters: [], // Clear podcast chapters for non-podcast media
			activeChapterIndex: -1,
			autoPlay: false, // Reset autoPlay when loading new media
			pendingRestore: null, // Clear any pending position restoration
			// Reset playback state to prevent mixing with previous media
			currentTime: 0,
			duration: 0,
			bufferedEnd: 0,
			isPaused: true,
			isPlaying: false,
		});
	},

	loadPodcast: async (
		podcastId: string,
		autoPlay = false,
		restoreProgress = true,
	) => {
		try {
			// Save current podcast progress before switching (fire and forget)
			const currentState = get();
			const previousPodcastId = currentState.podcastId;
			if (
				previousPodcastId &&
				previousPodcastId !== podcastId &&
				currentState.currentTime > 1 &&
				currentState.duration > 0
			) {
				console.debug(
					`[Player Store] Switching from podcast ${previousPodcastId.substring(0, 8)}... to ${podcastId.substring(0, 8)}... - saving progress at ${Math.floor(currentState.currentTime)}s`,
				);
				PodcastPlaybackProgressService.updateProgress(previousPodcastId, {
					currentTime: currentState.currentTime,
					duration: currentState.duration,
					completed: PodcastPlaybackProgressService.shouldMarkAsCompleted(
						currentState.currentTime,
						currentState.duration,
					),
				}).catch((error) => {
					console.error("Failed to save progress before switch:", error);
				});
			} else {
				console.debug(
					`[Player Store] Loading podcast ${podcastId.substring(0, 8)}...`,
				);
			}

			// Fetch podcast data and progress in parallel
			const [podcast, progressResult] = await Promise.allSettled([
				PodcastService.getPodcastById(podcastId),
				restoreProgress
					? PodcastPlaybackProgressService.getProgress(podcastId)
					: Promise.reject(new Error("Progress restore disabled")),
			]);

			if (podcast.status === "rejected") {
				throw podcast.reason;
			}

			const podcastData = podcast.value;
			const { url: audioUrl } =
				await PodcastService.getPodcastAudioUrl(podcastId);

			// Transform chapters for the player
			const chapters = PodcastService.transformChaptersForPlayer(
				podcastData.chapters,
				podcastData.length,
			);

			// Determine restore position if progress was found
			let restorePosition: number | null = null;
			if (progressResult.status === "fulfilled") {
				const progress = progressResult.value;
				const maxPosition = (progress.duration || podcastData.length) * 0.95;

				// Restore if not completed and has meaningful progress
				if (
					!progress.completed &&
					progress.currentTime > 1 &&
					progress.currentTime < maxPosition
				) {
					restorePosition = progress.currentTime;
				}
			}

			// Load media and update podcastId atomically to prevent race conditions
			// This ensures podcastId changes directly from old -> new without going through null
			set({
				currentMedia: {
					src: audioUrl,
					title: podcastData.title,
					subtitle: podcastData.podWorkspace?.title || "Podcast",
					chapters,
				},
				podcastId, // Set new podcast ID immediately
				podWorkspaceId: podcastData.podWorkspace?.id || null, // Set pod workspace ID
				chapters,
				podcastChapters: podcastData.chapters,
				activeChapterIndex: -1,
				pendingRestore: restorePosition
					? { podcastId, position: restorePosition }
					: null,
				autoPlay,
				// When (autoPlay === true) we want the UI to immediately show an
				// intent to play. The actual media element will start playback in
				// the ResponsiveMediaPlayer `onCanPlay` handler.
				isPaused: !autoPlay,
				isPlaying: !!autoPlay,
				// Reset playback state to prevent mixing with previous media
				currentTime: 0,
				duration: 0,
				bufferedEnd: 0,
				playbackSpeed: 1,
			});

			// Reset actual player speed
			const { playerRef } = get();
			if (playerRef) {
				playerRef.playbackRate = 1;
			}
		} catch (error) {
			console.error("Failed to load podcast:", error);
			throw error;
		}
	},

	goToNextChapter: () => {
		const { chapters, activeChapterIndex, duration } = get();

		if (chapters.length === 0) return;

		// If no active chapter, go to first
		if (activeChapterIndex < 0) {
			get().seek(chapters[0]?.startTime ?? 0);
			return;
		}

		// If at last chapter, go to end
		if (activeChapterIndex >= chapters.length - 1) {
			get().seek(duration);
			return;
		}

		// Go to next chapter
		const nextChapter = chapters[activeChapterIndex + 1];
		if (nextChapter) {
			get().seek(nextChapter.startTime);
		}
	},

	goToPreviousChapter: () => {
		const { chapters, activeChapterIndex } = get();

		if (chapters.length === 0) return;

		// If no active chapter or at first chapter, go to beginning
		if (activeChapterIndex <= 0) {
			get().seek(0);
			return;
		}

		// Go to previous chapter
		const prevChapter = chapters[activeChapterIndex - 1];
		if (prevChapter) {
			get().seek(prevChapter.startTime);
		}
	},

	goToChapter: (index) => {
		const { chapters } = get();

		if (index >= 0 && index < chapters.length) {
			const chapter = chapters[index];
			if (chapter) {
				get().seek(chapter.startTime);
			}
		}
	},

	setShowChapters: (show) => set({ showChapters: show }),

	toggleChaptersList: () =>
		set((state) => ({ showChapters: !state.showChapters })),

	toggleSpeedMenu: () =>
		set((state) => ({ showSpeedMenu: !state.showSpeedMenu })),

	// State update methods (called by player component)
	updatePlaybackState: (isPaused) => set({ isPaused, isPlaying: !isPaused }),

	updateCurrentTime: (time) => {
		set({ currentTime: time });
		// Update active chapter when time changes
		get().updateActiveChapter(time);
	},

	updateDuration: (duration) => set({ duration }),

	updateBufferedEnd: (bufferedEnd) => set({ bufferedEnd }),

	updateVolume: (volume) => set({ volume }),

	updateMuteState: (isMuted) => set({ isMuted }),

	updatePlaybackSpeed: (speed) => set({ playbackSpeed: speed }),

	updateActiveChapter: (time) => {
		const { chapters } = get();

		// Find the chapter that contains the current time
		const index = chapters.findIndex(
			(chapter) => time >= chapter.startTime && time < chapter.endTime,
		);

		set({ activeChapterIndex: index });
	},

	// Utility methods
	formatTime: (seconds) => {
		if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	},
}));

// ========================================
// Convenience Hooks & Selectors
// ========================================

/**
 * Get player actions directly from the store without subscribing to state changes.
 * This is NOT a React hook - it can be called anywhere, including event handlers and useEffect.
 *
 * Use this when you need to call actions but don't want to subscribe to state changes.
 * The returned object is always fresh and reflects the current store actions.
 *
 * @example
 * // In an event handler
 * onClick={() => {
 *   getPlayerActions().play();
 * }}
 *
 * @example
 * // In useEffect
 * useEffect(() => {
 *   getPlayerActions().loadMedia(media);
 * }, []);
 */
export const getPlayerActions = () => {
	const state = usePlayerStore.getState();
	return {
		setPlayerRef: state.setPlayerRef,
		play: state.play,
		pause: state.pause,
		togglePlay: state.togglePlay,
		seek: state.seek,
		seekRelative: state.seekRelative,
		setVolume: state.setVolume,
		toggleMute: state.toggleMute,
		setPlaybackSpeed: state.setPlaybackSpeed,
		loadMedia: state.loadMedia,
		loadPodcast: state.loadPodcast,
		goToNextChapter: state.goToNextChapter,
		goToPreviousChapter: state.goToPreviousChapter,
		goToChapter: state.goToChapter,
		setShowChapters: state.setShowChapters,
		toggleChaptersList: state.toggleChaptersList,
		toggleSpeedMenu: state.toggleSpeedMenu,
		seekToChapter: state.goToChapter, // Alias for convenience
		formatTime: state.formatTime,
		// State update methods (for syncing with Vidstack)
		updatePlaybackState: state.updatePlaybackState,
		updateCurrentTime: state.updateCurrentTime,
		updateDuration: state.updateDuration,
		updateBufferedEnd: state.updateBufferedEnd,
		updateVolume: state.updateVolume,
		updateMuteState: state.updateMuteState,
		updatePlaybackSpeed: state.updatePlaybackSpeed,
		updateActiveChapter: state.updateActiveChapter,
	};
};

/**
 * Commonly used selectors for derived state
 */
export const playerSelectors = {
	/**
	 * Get the currently active chapter object
	 */
	getCurrentChapter: (state: PlayerState): Chapter | null => {
		const { chapters, activeChapterIndex } = state;
		if (activeChapterIndex >= 0 && activeChapterIndex < chapters.length) {
			return chapters[activeChapterIndex] || null;
		}
		return null;
	},

	/**
	 * Get playback progress as a percentage (0-100)
	 */
	getProgressPercentage: (state: PlayerState): number => {
		const { currentTime, duration } = state;
		if (duration <= 0) return 0;
		return Math.min(100, Math.max(0, (currentTime / duration) * 100));
	},

	/**
	 * Get buffered progress as a percentage (0-100)
	 */
	getBufferedPercentage: (state: PlayerState): number => {
		const { bufferedEnd, duration } = state;
		if (duration <= 0) return 0;
		return Math.min(100, Math.max(0, (bufferedEnd / duration) * 100));
	},

	/**
	 * Get volume as a percentage (0-100)
	 */
	getVolumePercentage: (state: PlayerState): number => {
		const { volume } = state;
		return Math.round(Math.min(100, Math.max(0, volume * 100)));
	},

	/**
	 * Check if the player is at the last chapter
	 */
	isAtLastChapter: (state: PlayerState): boolean => {
		const { chapters, activeChapterIndex } = state;
		return chapters.length > 0 && activeChapterIndex === chapters.length - 1;
	},

	/**
	 * Check if the player is at the first chapter
	 */
	isAtFirstChapter: (state: PlayerState): boolean => {
		const { activeChapterIndex } = state;
		return activeChapterIndex === 0;
	},

	/**
	 * Get chapter markers with their positions as percentages for UI rendering
	 */
	getChapterMarkers: (state: PlayerState) => {
		const { chapters, duration } = state;
		if (duration <= 0) return [];

		return chapters.map((chapter, index) => ({
			position: (chapter.startTime / duration) * 100,
			chapter,
			index,
		}));
	},
};
